import { IconButton, type SxProps, type Theme } from "@mui/material";
import { PlusIcon, MinusIcon } from "@phosphor-icons/react";

const buttonStyles: SxProps<Theme> = {
  border: "1px solid",
  borderColor: "var(--color-surface-accent-strong)",
  color: "var(--color-surface-accent-strong)",
  transition: "border-color 0.2s, color 0.2s",

  "&.Mui-disabled": {
    borderColor: "var(--color-surface-disabled-accent-strong)",
    color: "var(--color-surface-disabled-accent-strong)",
  },
};

interface CounterProps {
  value: number;
  step?: number;
  maxValue?: number;
  minValue?: number;
  plusButtonAriaLabel?: string;
  minusButtonAriaLabel?: string;
  plusButtonDisabled?: boolean;
  minusButtonDisabled?: boolean;
  onChange: (newValue: number) => void;
}

export function CounterButton({
  value,
  step = 1,
  maxValue,
  minValue = 0,
  plusButtonAriaLabel,
  minusButtonAriaLabel,
  plusButtonDisabled,
  minusButtonDisabled,
  onChange,
}: CounterProps) {
  const minusDisabled = minusButtonDisabled || value <= minValue;
  const plusDisabled =
    plusButtonDisabled || (maxValue != null && value >= maxValue);

  return (
    <div className="flex w-fit items-center gap-2 text-text-primary">
      <IconButton
        onClick={() => onChange(Math.max(value - step, minValue))}
        disabled={minusDisabled}
        type="button"
        aria-label={minusButtonAriaLabel ?? "-"}
        sx={buttonStyles}
        size="small"
      >
        <MinusIcon size={16} color="currentColor" />
      </IconButton>

      <span className="flex w-8 justify-center text-text-primary">{value}</span>

      <IconButton
        onClick={() => {
          const newValue = value + step;
          onChange(maxValue != null ? Math.min(newValue, maxValue) : newValue);
        }}
        disabled={plusDisabled}
        type="button"
        aria-label={plusButtonAriaLabel ?? "+"}
        sx={buttonStyles}
        size="small"
      >
        <PlusIcon size={16} color="currentColor" />
      </IconButton>
    </div>
  );
}
